import React, { useMemo } from "react";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { BattleStatsRecordPerTick } from "types/battle-engine";
import { S3_BASE_URL } from "@/utils/s3";

interface PokemonStats {
  damage_done: number;
  healing_done: number;
  damage_taken: number;
}

interface BattleStatsHUDProps {
  battleState: Record<string, ClientPokemon>;
  // Optional props to match potential usage, or define strict if we have data
  teamA?: any[];
  teamB?: any[];
  // Legacy props that internal logic might still need, can be derived or optional
  battleStats?: BattleStatsRecordPerTick;
  currentTick?: number;
}

interface PokemonRanking {
  pokemonId: string;
  pokemon: ClientPokemon;
  totalScore: number;
  damage_done: number;
  healing_done: number;
  damage_taken: number;
}

/**
 * Live battle statistics HUD showing pokemon rankings
 */
export const BattleStatsHUD: React.FC<BattleStatsHUDProps> = ({
  battleState,
  battleStats = {}, // Default empty object
  currentTick = 0, // Default to 0
}) => {
  // Map battleState to internal pokemons variable
  const pokemons = battleState;

  /**
   * Pre-calculate cumulative stats for all ticks once
   */
  const cumulativeStats = useMemo(() => {
    const cumulative: Record<number, Record<string, PokemonStats>> = {};
    const currentTotals: Record<string, PokemonStats> = {};

    // Get sorted ticks
    const ticks = Object.keys(battleStats)
      .map(Number)
      .sort((a, b) => a - b);

    for (const tick of ticks) {
      const pokemonTickStats = battleStats[tick];
      if (!pokemonTickStats) continue;

      for (const [pokemonId, stats] of Object.entries(pokemonTickStats)) {
        if (!currentTotals[pokemonId]) {
          currentTotals[pokemonId] = {
            damage_done: 0,
            healing_done: 0,
            damage_taken: 0,
          };
        }

        currentTotals[pokemonId].damage_done += stats.damage_done;
        currentTotals[pokemonId].healing_done += stats.healing_done;
        currentTotals[pokemonId].damage_taken += stats.damage_taken;
      }

      // Store a snapshot of current totals for this tick
      cumulative[tick] = {};
      for (const [pokemonId, totals] of Object.entries(currentTotals)) {
        cumulative[tick][pokemonId] = { ...totals };
      }
    }

    return cumulative;
  }, [battleStats]);

  /**
   * Get rankings for the current tick using pre-calculated cumulative stats
   */
  const rankings = useMemo((): {
    teamARankings: PokemonRanking[];
    teamBRankings: PokemonRanking[];
    allRankings: PokemonRanking[];
  } => {
    // Find the closest tick in cumulativeStats that is <= currentTick
    const ticks = Object.keys(cumulativeStats)
      .map(Number)
      .sort((a, b) => b - a); // Sort descending to find the largest tick <= currentTick
    const bestTick = ticks.find((t) => t <= currentTick) ?? 0;

    const aggregatedStats = cumulativeStats[bestTick] || {};

    // Build rankings array
    const rankingsArray: PokemonRanking[] = [];

    for (const [pokemonId, pokemon] of Object.entries(pokemons)) {
      const stats = aggregatedStats[pokemonId] || {
        damage_done: 0,
        healing_done: 0,
        damage_taken: 0,
      };

      const totalScore = stats.damage_done;

      rankingsArray.push({
        pokemonId,
        pokemon,
        totalScore,
        ...stats,
      });
    }

    // Sort by total score (highest first)
    const sortedRankings = rankingsArray.sort(
      (a, b) => b.totalScore - a.totalScore,
    );

    const teamARankings = sortedRankings.filter(
      (r) => (r.pokemon as any).Team === "A",
    );
    const teamBRankings = sortedRankings.filter(
      (r) => (r.pokemon as any).Team === "B",
    );

    return {
      teamARankings,
      teamBRankings,
      allRankings: sortedRankings,
    };
  }, [pokemons, cumulativeStats, currentTick]);

  // Find max score for bar scaling
  const maxScore = useMemo(() => {
    return Math.max(...rankings.allRankings.map((r) => r.totalScore), 1);
  }, [rankings]);

  return (
    <div className="pointer-events-none">
      <div
        className=""
        style={{
          position: "absolute",
          top: "2px",
          left: "2px",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          borderRadius: "8px",
          padding: "12px",
          fontFamily: "monospace",
          color: "white",
          zIndex: 1000,
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
        }}
      >
        {rankings.teamARankings.map((ranking, index) => {
          const barWidth = (ranking.totalScore / maxScore) * 100;
          const parsedPokemonName = parsePokeNameForSprite(
            ranking.pokemon.base_pokemon_name,
          );

          return (
            <div
              key={ranking.pokemonId}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
                transition: "all 0.3s ease-in-out",
              }}
            >
              {/* Pokemon sprite in blue box */}
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  backgroundColor: "#1976d2",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "8px",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <img
                  src={`${S3_BASE_URL}/pokemons/${parsedPokemonName}/portraits/Normal.png`}
                  alt={ranking.pokemon.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    imageRendering: "pixelated",
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't load
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>

              {/* Score bar */}
              <div className="flex border-3 border-white/80 w-18 relative h-6 bg-black/80 rounded-md overflow-hidden">
                {/* Filled bar */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${barWidth}%`,
                    background:
                      (ranking.pokemon as any).Team === `B`
                        ? `linear-gradient(90deg, #2196f3, #1976d2)`
                        : `linear-gradient(90deg, #ff3333, #ff6666)`, // Blue for teamA, Red for teamB
                    transition: "width 0.3s ease-in-out",
                  }}
                />

                {/* Score text */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "white",
                    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
                    zIndex: 1,
                  }}
                >
                  {Math.round(ranking.totalScore).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div
        className=""
        style={{
          position: "absolute",
          top: "2px",
          right: "2px",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          borderRadius: "8px",
          padding: "12px",
          fontFamily: "monospace",
          color: "white",
          zIndex: 1000,
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.5)",
        }}
      >
        {rankings.teamBRankings.map((ranking, index) => {
          const barWidth = (ranking.totalScore / maxScore) * 100;

          const parsedPokemonName = parsePokeNameForSprite(
            ranking.pokemon.base_pokemon_name,
          );

          return (
            <div
              key={ranking.pokemonId}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
                transition: "all 0.3s ease-in-out",
              }}
            >
              {/* Score bar */}
              <div className="flex border-3 border-white/80 w-18 relative h-6 bg-black/80 rounded-md overflow-hidden">
                {/* Filled bar */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${barWidth}%`,
                    background:
                      (ranking.pokemon as any).Team === `B`
                        ? `linear-gradient(90deg, #2196f3, #1976d2)`
                        : `linear-gradient(90deg, #ff3333, #ff6666)`, // Blue for teamA, Red for teamB
                    transition: "width 0.3s ease-in-out",
                  }}
                />

                {/* Score text */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: "white",
                    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
                    zIndex: 1,
                  }}
                >
                  {Math.round(ranking.totalScore).toLocaleString()}
                </div>
              </div>

              {/* Pokemon sprite in blue box */}
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  backgroundColor: "#1976d2",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "8px",
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                <img
                  src={`${S3_BASE_URL}/pokemons/${parsedPokemonName}/portraits/Normal.png`}
                  alt={ranking.pokemon.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    imageRendering: "pixelated",
                  }}
                  onError={(e) => {
                    // Fallback if image doesn't load
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
